import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { JsonObjectFactoryService } from '../../../shared/_services/json-object-factory.service';
import { F163jReclassTriggerService } from './f163j-reclass-trigger.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-163j-reclass-trigger',
    templateUrl: './f163j-reclass-trigger.component.html',
    styleUrls: ['./f163j-reclass-trigger.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class F163jReclassTriggerComponent implements OnInit, OnDestroy {
  private static readonly bridgeHostSelector = 'gtw-163j-reclass-trigger-ce';
  private readonly subs = new Subscription();

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  appService: any = {
    USER_SETTINGS: '',
    SERVER_CONFIG: ''
  };

  rowData: any = {};
  filterParams: any = {};
  baseURLs: GtwElementsBaseUrlsModel = {} as GtwElementsBaseUrlsModel;
  globalService: any;
  GENERAL_CONFIG: any;
  workspaceData: any[] = [];

  userSettings: any = this.appService.USER_SETTINGS;
  logged_in_user: string = '';
  currentyear: string = '';

  showModal = true;
  modalConfig: ConfirmationModalConfig = {
    title: 'Confirm',
    message: 'Are you sure you want to reclass?',
    iconClass: 'fa fa-sign-out',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
    animationClass: 'animated fadeIn fast',
    loading: false,
    loadingText: 'Processing...'
  };

  private processing = false;

  constructor(
    private jsonObjectFactory: JsonObjectFactoryService,
    private f163jReclassTriggerService: F163jReclassTriggerService,
    private _snackBar: MatSnackBar,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header;
    this.appService = {
      USER_SETTINGS: header.userSettingsData || {},
      SERVER_CONFIG: header.serverConfigData || {},
    };
    const d = activeScreen?.data;
    if (Array.isArray(d?.data)) {
      this.workspaceData = d.data;
    } else if (Array.isArray(d)) {
      this.workspaceData = d;
    } else {
      this.workspaceData = [];
    }
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.cdr.markForCheck();
  }

  init(): void {
    try {
      if (!this.globalService || !this.GENERAL_CONFIG) {
        return;
      }

      this.userSettings = this.appService.USER_SETTINGS;
      this.logged_in_user = this.userSettings?.user?.sso_id || '';

      if (this.rowData && Object.keys(this.rowData).length > 0) {
        this.currentyear = this.rowData.TAX_YEAR || this.globalService?.globalParams?.tax_year || '';
      }
    } catch (e) {
      console.error('Error in F163jReclassTrigger init:', e);
    }
  }

  private processWorkspaceData(): any[] {
    if (!this.workspaceData || !Array.isArray(this.workspaceData)) {
      return [];
    }

    const dataToSend: any[] = [];

    this.workspaceData.forEach(element => {
      if (element.INCLUDE_163J_YN == 1 &&
          element.LINE_32 != 0 &&
          (element.HO_LE_TAX_TYPE == 'PTSP' ||
           element.HO_LE_TAX_TYPE == 'NCPTSP' ||
           element.HO_LE_TAX_TYPE == 'NCPTSPNF' ||
           element.HO_LE_TAX_TYPE == 'DPS')) {

        dataToSend.push({
          tax_year: element.TAX_YEAR,
          ho_combination_key: element.COMBINATION_KEY,
          ho_leid: element.HO_LEID,
          ho_cdr_no: element.HO_CDR_NO,
          ho_rp: element.HO_REPORTING_PERIOD,
          ho_functional_currency: element.HO_FUNCTIONAL_CURRENCY,
          line_32_amt: element.LINE_32,
          le_tax_type: element.HO_LE_TAX_TYPE,
          source_system_flag: element.SOURCE_SYSTEM_FLAG
        });
      }
    });

    return dataToSend;
  }

  onConfirm(): void {
    this.confirmSave();
  }

  onCancel(): void {
    this.cancel();
  }

  cancel(): void {
    this.dispatchGridUpdate('cancel');
  }

  private dispatchGridUpdate(detail: 'cancel' | 'refresh'): void {
    this.showModal = false;
    this.requestGridUpdate.emit(detail);
    this.cdr.markForCheck();
  }

  private setModalLoading(loading: boolean): void {
    this.modalConfig = { ...this.modalConfig, loading };
    this.cdr.markForCheck();
  }

  confirmSave(): void {
    if (this.processing) {
      return;
    }

    this.processing = true;
    this.setModalLoading(true);

    const dataToSend = this.processWorkspaceData();

    if (dataToSend.length > 0) {
      const returnClobSettingsObj = {
        sso_id: this.logged_in_user,
        tax_year: this.rowData.TAX_YEAR || this.currentyear,
        scenario: this.rowData.SCENARIO || this.globalService?.globalParams?.scenario,
        jcd_key: this.rowData.JCD_KEY || this.globalService?.globalParams?.jcd_key
      };

      const sub = this.f163jReclassTriggerService
        .saveReclassDetails(returnClobSettingsObj, dataToSend, this.globalService, this.GENERAL_CONFIG)
        .subscribe(
          (result: any) => {
            this.processing = false;
            this.setModalLoading(false);

            if (result.callSuccess == '1') {
              this._snackBar.open('Success!', 'Close', {
                duration: 4000,
                panelClass: ['success-snackbar']
              });
              this.dispatchGridUpdate('refresh');
            } else {
              this._snackBar.open(result.errorMessage || 'An error occurred during processing.', 'Close', {
                duration: 4000,
                panelClass: ['error-snackbar']
              });
            }
          },
          () => {
            this.processing = false;
            this.setModalLoading(false);
            this._snackBar.open('Error occurred while processing. Please try again.', 'Close', {
              duration: 4000,
              panelClass: ['error-snackbar']
            });
          }
        );
      this.subs.add(sub);
    } else {
      this.processing = false;
      this.setModalLoading(false);

      this._snackBar.open('No data eligible to reclass!', 'Close', {
        duration: 4000,
        panelClass: ['error-snackbar']
      });

      this.dispatchGridUpdate('refresh');
    }
  }
}
